import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import {
  getUpcomingMilestones,
  type InquiryDto,
  type OfferDto,
  type ProjectDto,
  type ProjectLastActivityKind,
  type ProjectActivityEventDto,
} from '@paladin-monorepo/common';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Contact } from '@/components/Contact';
import { Timeline } from '@/components/Timeline';
import { projectsApi } from '@/lib/api/projects';
import { Play } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { MarkdownContent } from '@/components/llm-chat/MarkdownContent';
import { OpenProjectAgentButton } from '@/components/project-agent/OpenProjectAgentButton';
import { ProjectGovernancePanel } from '@/components/project-governance/ProjectGovernancePanel';
import { ProjectChangeAdoptionCard } from '@/components/project-governance/ProjectChangeAdoptionCard';
import { useAuth } from '@/lib/auth';
import { isPrince2Account } from '@/lib/permissions';

function lastActivityKindKey(kind: ProjectLastActivityKind): string {
  return `workspace.header.lastActivityKind.${kind}`;
}

function activityDeepLink(projectId: string, ev: ProjectActivityEventDto): string {
  const base = `/projects/${projectId}/workspace`;
  if (ev.kind === 'workspace_file') {
    return `${base}/documents?fileId=${encodeURIComponent(ev.fileId)}`;
  }
  const tab = ev.targetKind === 'milestone' ? 'milestones' : 'documents';
  return `${base}/${tab}?commentId=${encodeURIComponent(ev.commentId)}&targetKind=${encodeURIComponent(ev.targetKind)}&targetId=${encodeURIComponent(ev.targetId)}`;
}

const milestoneStatusI18n: Record<string, string> = {
  NotStarted: 'notStarted',
  InProgress: 'inProgress',
  Handover: 'handover',
  Completed: 'completed',
  Blocked: 'blocked',
};

export function ProjectOverviewPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectOverviewPage' });
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { inquiry, offer, project } = useOutletContext<{ inquiry?: InquiryDto; offer?: OfferDto; project: ProjectDto }>();
  const queryClient = useQueryClient();
  const milestonesPath = projectId ? `/projects/${projectId}/workspace/milestones` : '#';
  const upcoming = getUpcomingMilestones(project.milestones, 3);
  const activitySummary =
    project.lastActivityAt && project.lastActivityKind
      ? t('workspace.header.lastActivity', {
          relative: formatRelativeTime(project.lastActivityAt, i18n.language),
          kind: t(lastActivityKindKey(project.lastActivityKind)),
        })
      : offer?.updatedAt
        ? t('workspace.header.lastUpdated', { date: formatDate(offer.updatedAt) })
        : t('generic.unspecified');

  const startProjectMutation = useMutation({
    mutationFn: () => projectsApi.startProject(project.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });

  const { data: projectActivity = [], isLoading: projectActivityLoading } = useQuery({
    queryKey: ['projectActivity', project.id],
    enabled: !!project.id,
    queryFn: () => projectsApi.getProjectActivity(project.id),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {project.status === 'Kickoff' && (
        <Card className="lg:col-span-3 border-primary/30 bg-primary/5">
          <CardContent className="flex items-center justify-between py-4">
            <div>
              <h3 className="font-medium">{t('workspace.overview.readyToStart')}</h3>
              <p className="text-sm text-muted-foreground">{t('workspace.overview.readyToStartDescription')}</p>
            </div>
            <Button
              onClick={() => startProjectMutation.mutate()}
              disabled={startProjectMutation.isPending}
            >
              <Play className="w-4 h-4 mr-2" />
              {t('workspace.overview.startProject')}
            </Button>
          </CardContent>
        </Card>
      )}
      <ProjectGovernancePanel project={project} />
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle>{t('workspace.overview.dashboardTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <div className="flex justify-between text-xs mb-1 text-muted-foreground">
                <span className="font-medium">{t('workspace.overview.dashboardProgress')}</span>
                <span>{project.progress}%</span>
              </div>
              <Progress value={project.progress} />
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">
                {t('workspace.overview.dashboardUpcoming')}
              </div>
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t('generic.unspecified')}</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {upcoming.map((m) => (
                    <li key={m.id} className="flex flex-col gap-0.5">
                      <span className="font-medium text-foreground">{m.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {m.dueDate ? formatDate(m.dueDate) : t('generic.unspecified')}
                        {' · '}
                        <Badge variant="secondary" className="text-[10px]">
                          {t(`workspace.milestones.status.${milestoneStatusI18n[m.status] ?? 'notStarted'}`)}
                        </Badge>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <Link
                to={milestonesPath}
                className="inline-block text-xs text-primary hover:underline mt-2"
              >
                {t('workspace.overview.viewMilestones')}
              </Link>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('workspace.overview.dashboardLastActivity')}
              </div>
              <p className="text-sm text-foreground">{activitySummary}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle>{t('workspace.overview.activityTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {projectActivityLoading ? (
            <p className="text-sm text-muted-foreground">{t('workspace.files.loading')}</p>
          ) : projectActivity.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('workspace.overview.activityEmpty')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {projectActivity.map((ev, i) => (
                <li
                  key={
                    ev.kind === 'workspace_file'
                      ? `f-${ev.fileId}-${ev.fileKind}-${ev.at}-${i}`
                      : `c-${ev.commentId}-${ev.at}-${i}`
                  }
                  className="flex flex-wrap items-start gap-x-2 gap-y-1 border-b border-border/60 pb-2 last:border-0"
                >
                  <span className="text-muted-foreground shrink-0">
                    {new Date(ev.at).toLocaleString(i18n.language)}
                  </span>
                  <span className="font-medium shrink-0">{ev.actor.displayName}</span>
                  <span className="text-foreground/90">
                    {ev.kind === 'workspace_file'
                      ? ev.fileKind === 'uploaded'
                        ? t('workspace.overview.activityKind.fileUploaded')
                        : t('workspace.overview.activityKind.fileDeleted')
                      : t('workspace.overview.activityKind.comment')}
                  </span>
                  <span className="text-muted-foreground min-w-0 flex-1 basis-full sm:basis-auto">
                    {ev.kind === 'workspace_file' ? ev.originalName : ev.bodySnippet}
                  </span>
                  {projectId && (
                    <Link
                      to={activityDeepLink(projectId, ev)}
                      className="text-primary text-xs font-medium hover:underline shrink-0"
                    >
                      {t('workspace.overview.activityOpen')}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>{t('workspace.overview.projectSummary')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {inquiry?.objectives ? (
            <MarkdownContent content={inquiry.objectives} className="text-sm text-muted-foreground" />
          ) : (
            <p className="text-sm text-muted-foreground">{t('workspace.overview.noInquirySummary')}</p>
          )}
          {project.inquiryId ? (
            <OpenProjectAgentButton inquiryId={project.inquiryId} />
          ) : project.projectConceptId ? (
            <OpenProjectAgentButton projectConceptId={project.projectConceptId} />
          ) : null}
          {project.projectConceptId && !project.inquiryId && (
            <Button
              variant="outline"
              onClick={() => navigate(`/inquiries/new?conceptId=${encodeURIComponent(project.projectConceptId!)}`)}
            >
              {t('workspace.overview.createInquiry')}
            </Button>
          )}
          {project.inquiryId && (
            <div>
              <div className="text-sm font-medium mb-2">
                {t('workspace.overview.phases')}
              </div>
              <Timeline status={project.status} />
            </div>
          )}
        </CardContent>
      </Card>
      <div className="space-y-4">
        {(inquiry || offer) && (
        <Card>
          <CardHeader>
            <CardTitle>{t('workspace.overview.keyContacts')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {inquiry && (
              <div className='flex flex-col gap-2'>
                <h3 className="text-lg font-medium">
                  {t('workspace.overview.inquiryKeyContact')}
                </h3>
                {inquiry.keyContact && (
                  <Contact profile={inquiry.keyContact} />
                )}
              </div>
              )}
              {offer && (
              <div>
                <h3 className="text-lg font-medium pb-2">
                  {t('workspace.overview.offerKeyContact')}
                </h3>
                {offer.keyContact && (
                  <Contact profile={offer.keyContact} />
                )}
              </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {isPrince2Account(user) && projectId ? <ProjectChangeAdoptionCard projectId={projectId} /> : null}

        {/* <Card>
          <CardHeader>
            <CardTitle>{t('workspace.overview.aiAssistantTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-2 text-sm">
              <Lightbulb className="w-4 h-4 text-primary mt-1" />
              <p>{t('workspace.overview.aiAssistantText')}</p>
            </div>
          </CardContent>
        </Card> */}
      </div>
    </div>
  );
}

ProjectOverviewPage.displayName = 'ProjectOverviewPage';
